import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSocket } from "../../context/SocketContext";
import api from "../../api/riderApi";

import PickupStep from "./trip/PickupStep";
import TransitStep from "./trip/TransitStep";
import DeliveryStep from "./trip/DeliveryStep";
import RatingStep from "./trip/RatingStep";
import CancelDeliveryModal from "./trip/CancelDeliveryModal";

export default function ActiveTrip() {
  const navigate = useNavigate();
  const socket = useSocket();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [completed, setCompleted] = useState(false);

  // Seller rating state
  const [sellerRating, setSellerRating] = useState(0);
  const [sellerComment, setSellerComment] = useState("");
  const [ratingSubmitting, setRatingSubmitting] = useState(false);
  const [ratingSubmitted, setRatingSubmitted] = useState(false);

  // Cancel delivery state
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  // Delivery verification sub-flow state: null | 'otp' | 'photo' | 'cod' | 'confirm'
  const [verifyStep, setVerifyStep] = useState(null);
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [photoTaken, setPhotoTaken] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [codCollected, setCodCollected] = useState(false);
  const [codAmount, setCodAmount] = useState("");
  const [codMethod, setCodMethod] = useState("cash");

  const fileInputRef = useRef(null);
  const otpRefs = [useRef(), useRef(), useRef(), useRef()];

  const cancelReasons = [
    "Buyer unavailable",
    "Wrong address",
    "Buyer refused",
    "Other",
  ];

  useEffect(() => {
    api
      .get("/trips/active")
      .then((res) => {
        if (!res.data) navigate("/rider");
        else setTrip(res.data);
      })
      .catch(() => navigate("/rider"))
      .finally(() => setLoading(false));
  }, [navigate]);

  useEffect(() => {
    if (!socket) return;
    const handleCancelled = (data) => {
      if (trip && data.order_id === trip.id) {
        alert("This trip has been cancelled by the customer.");
        navigate("/rider");
      }
    };
    socket.on("trip:cancelled", handleCancelled);
    return () => socket.off("trip:cancelled", handleCancelled);
  }, [socket, trip, navigate]);

  const advanceStatus = async (status) => {
    setUpdating(true);
    try {
      const res = await api.put(`/trips/${trip.id}/status`, { status });
      setTrip(res.data);
    } catch (err) {
      alert(err.response?.data?.error || "Failed to update status.");
    } finally {
      setUpdating(false);
    }
  };

  const handleArrivedAtDropoff = () => {
    setVerifyStep("otp");
    setTimeout(() => otpRefs[0].current?.focus(), 200);
  };

  const handleCancelDelivery = async () => {
    if (!cancelReason) return;
    setCancelling(true);
    try {
      await api.post(`/trips/${trip.id}/cancel`, { reason: cancelReason });
      if (socket)
        socket.emit("rider:status_update", {
          order_id: trip.id,
          status: "cancelled",
        });
      navigate("/rider");
    } catch (err) {
      alert(err.response?.data?.error || "Failed to cancel delivery.");
    } finally {
      setCancelling(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) value = value.slice(-1);
    if (value && !/^\d$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setOtpError("");
    if (value && index < 3) {
      otpRefs[index + 1].current?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs[index - 1].current?.focus();
    }
  };

  const verifyOtp = async () => {
    const code = otp.join("");
    if (code.length !== 4) {
      setOtpError("Please enter all 4 digits.");
      return;
    }
    setUpdating(true);
    try {
      const res = await api.post(`/trips/${trip.id}/verify-otp`, { otp: code });
      if (res.data.verified) {
        setOtpVerified(true);
        setVerifyStep("photo");
      }
    } catch (err) {
      setOtpError(err.response?.data?.error || "Verification failed.");
      setOtp(["", "", "", ""]);
      setTimeout(() => otpRefs[0].current?.focus(), 200);
    } finally {
      setUpdating(false);
    }
  };

  const handleTakePhoto = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoSelected = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const maxWidth = 1024;
        const maxHeight = 1024;
        let { width, height } = img;
        if (width > maxWidth) {
          height *= maxWidth / width;
          width = maxWidth;
        }
        if (height > maxHeight) {
          width *= maxHeight / height;
          height = maxHeight;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              setPhotoFile(
                new File([blob], "delivery-photo.jpg", { type: "image/jpeg" }),
              );
              setPhotoPreview(URL.createObjectURL(blob));
            }
          },
          "image/jpeg",
          0.7,
        );
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  const uploadPhoto = async () => {
    if (!photoFile) return;
    setPhotoUploading(true);
    try {
      const formData = new FormData();
      formData.append("photo", photoFile);
      formData.append("taken_at", new Date().toISOString());

      if (navigator.geolocation) {
        try {
          const pos = await new Promise((resolve, reject) =>
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              timeout: 5000,
            }),
          );
          formData.append("lat", pos.coords.latitude);
          formData.append("lng", pos.coords.longitude);
        } catch {}
      }

      await api.post(`/trips/${trip.id}/upload-photo`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhotoTaken(true);

      if (trip.cod_enabled) {
        setVerifyStep("cod");
        setCodAmount(parseFloat(trip.cod_amount).toFixed(2));
      } else {
        setVerifyStep("confirm");
      }
    } catch (err) {
      alert(
        err.response?.data?.error ||
          "Failed to upload photo. Please try again.",
      );
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleRetakePhoto = () => {
    setPhotoPreview(null);
    setPhotoFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const recordCod = async () => {
    if (!codAmount || parseFloat(codAmount) <= 0) {
      alert("Please enter the amount collected.");
      return;
    }
    setUpdating(true);
    try {
      await api.post(`/trips/${trip.id}/cod-collect`, {
        amount_collected: parseFloat(codAmount),
        collection_method: codMethod,
      });
      setCodCollected(true);
      setVerifyStep("confirm");
    } catch (err) {
      alert(err.response?.data?.error || "Failed to record collection.");
    } finally {
      setUpdating(false);
    }
  };

  const completeDelivery = async () => {
    setUpdating(true);
    try {
      await api.post(`/trips/${trip.id}/complete`);
      if (socket)
        socket.emit("rider:status_update", {
          order_id: trip.id,
          status: "delivered",
        });
      setCompleted(true);
      if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
    } catch (err) {
      alert(err.response?.data?.error || "Failed to complete delivery.");
    } finally {
      setUpdating(false);
    }
  };

  const submitSellerRating = async () => {
    if (!sellerRating) return;
    setRatingSubmitting(true);
    try {
      await api.post(`/trips/${trip.id}/rate-seller`, {
        rating: sellerRating,
        comment: sellerComment.trim() || null,
      });
      setRatingSubmitted(true);
    } catch (err) {
      alert(err.response?.data?.error || "Failed to submit rating.");
    } finally {
      setRatingSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
      </div>
    );
  }

  if (!trip) return null;

  if (completed) {
    return (
      <RatingStep
        sellerName={trip.seller_name}
        rating={sellerRating}
        onRatingChange={setSellerRating}
        comment={sellerComment}
        onCommentChange={setSellerComment}
        onSubmit={submitSellerRating}
        onSkip={() => navigate("/rider")}
        submitting={ratingSubmitting}
        submitted={ratingSubmitted}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 px-5 py-6 max-w-lg mx-auto pb-24">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-bold text-gray-900">
          Order #{trip.order_number}
        </h1>
        <div className="flex gap-2">
          {trip.express && (
            <span className="bg-red-100 text-red-700 px-2.5 py-0.5 rounded-full text-xs font-semibold">
              Express
            </span>
          )}
          {trip.cod_enabled && (
            <span className="bg-amber-100 text-amber-800 px-2.5 py-0.5 rounded-full text-xs font-semibold">
              COD
            </span>
          )}
        </div>
      </div>

      {trip.status === "ASSIGNED" && (
        <PickupStep
          trip={trip}
          onMarkPickedUp={() => advanceStatus("PICKED_UP")}
          updating={updating}
          onOpenCancelModal={() => setShowCancelDialog(true)}
        />
      )}

      {(trip.status === "PICKED_UP" ||
        (trip.status === "IN_TRANSIT" && !verifyStep)) && (
        <TransitStep
          trip={trip}
          onArrivedAtDropoff={handleArrivedAtDropoff}
          updating={updating}
          onOpenCancelModal={() => setShowCancelDialog(true)}
        />
      )}

      {trip.status === "IN_TRANSIT" && verifyStep && (
        <DeliveryStep
          trip={trip}
          verifyStep={verifyStep}
          setVerifyStep={setVerifyStep}
          otp={otp}
          onOtpChange={handleOtpChange}
          onOtpKeyDown={handleOtpKeyDown}
          onVerifyOtp={verifyOtp}
          otpRefs={otpRefs}
          otpError={otpError}
          otpVerified={otpVerified}
          fileInputRef={fileInputRef}
          photoPreview={photoPreview}
          onPhotoSelected={handlePhotoSelected}
          onTakePhoto={handleTakePhoto}
          onRetakePhoto={handleRetakePhoto}
          onUploadPhoto={uploadPhoto}
          photoUploading={photoUploading}
          photoTaken={photoTaken}
          codAmount={codAmount}
          onCodAmountChange={setCodAmount}
          codMethod={codMethod}
          onCodMethodChange={setCodMethod}
          onRecordCod={recordCod}
          codCollected={codCollected}
          onCompleteDelivery={completeDelivery}
          updating={updating}
        />
      )}

      <CancelDeliveryModal
        open={showCancelDialog}
        reasons={cancelReasons}
        reason={cancelReason}
        onReasonChange={setCancelReason}
        onConfirm={handleCancelDelivery}
        onClose={() => setShowCancelDialog(false)}
        cancelling={cancelling}
      />
    </div>
  );
}
